import { Dropdown } from "colbi_web_ui/lib/components/inputs";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { SovosButton } from "mosaic-react";
import React, { useEffect, useRef, useState } from "react";
import {
    getProjectScriptsAction,
    runProjectScriptAction,
} from "../../../../store/actions/adminScriptsActions";
import styles from "./AdminScriptsTab.module.sass";

const AdminScriptsTab = ({ projectId, dispatch }) => {
    const { i18n } = useGlobalValue();
    const mountedRef = useRef(true);
    const projectIdRef = useRef(projectId);
    const [scripts, setScripts] = useState([]);
    const [loadingScripts, setLoadingScripts] = useState(Boolean(projectId));
    const [selected, setSelected] = useState(null);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    projectIdRef.current = projectId;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        let active = true;
        setError(null);
        setResult(null);
        setRunning(false);
        setScripts([]);
        setSelected(null);

        if (!projectId) {
            setLoadingScripts(false);
            return () => {
                active = false;
            };
        }

        setLoadingScripts(true);
        const loadScripts = async () => {
            try {
                const response = await dispatch(getProjectScriptsAction());
                if (!active) {
                    return;
                }
                if (response?.errors?.length) {
                    setError(i18n`Error loading scripts`);
                    return;
                }
                setScripts(response?.data?.projectScripts || []);
            } catch {
                if (active) {
                    setError(i18n`Error loading scripts`);
                }
            } finally {
                if (active) {
                    setLoadingScripts(false);
                }
            }
        };
        loadScripts();

        return () => {
            active = false;
        };
    }, [dispatch, i18n, projectId]);

    const options = scripts.map((script) => ({ id: script.file, name: script.name }));

    const handleRun = async () => {
        if (!selected || !projectId) {
            return;
        }

        setRunning(true);
        setError(null);
        setResult(null);
        const requestedProjectId = String(projectId);

        try {
            const response = await dispatch(
                runProjectScriptAction(requestedProjectId, selected.file),
            );
            if (!mountedRef.current || String(projectIdRef.current) !== requestedProjectId) {
                return;
            }
            const { data, errors } = response || {};

            if (errors && errors.length) {
                setError(i18n`Error running script`);
            } else if (data?.runProjectScript?.success) {
                setResult(data.runProjectScript);
            } else {
                setError(data?.runProjectScript?.message || i18n`Error running script`);
            }
        } catch {
            if (mountedRef.current && String(projectIdRef.current) === requestedProjectId) {
                setError(i18n`Error running script`);
            }
        } finally {
            if (mountedRef.current && String(projectIdRef.current) === requestedProjectId) {
                setRunning(false);
            }
        }
    };

    return (
        <div className={styles["admin-scripts-tab"]}>
            <h5 className={styles["admin-scripts-title"]}>{i18n`Run Scripts`}</h5>
            {loadingScripts ? <small>{i18n`Loading scripts`}…</small> : null}
            {!loadingScripts && error && !scripts.length ? (
                <div className={styles["admin-scripts-error"]}>{error}</div>
            ) : null}
            {!loadingScripts && !error && !scripts.length ? (
                <small>{i18n`No scripts available`}</small>
            ) : null}
            {!loadingScripts && scripts.length ? (
                <>
                    <div className={styles["admin-scripts-field"]}>
                        <Dropdown
                            value={selected ? { id: selected.file, name: selected.name } : null}
                            options={options}
                            className={styles["admin-scripts-dropdown"]}
                            placeholder={i18n`Select a script`}
                            filter
                            onChange={(e) => {
                                const script = scripts.find((s) => s.file === e.target.value?.id);
                                setSelected(script || null);
                                setResult(null);
                                setError(null);
                            }}
                        />
                        {selected && selected.description ? (
                            <small className={styles["admin-scripts-description"]}>
                                {selected.description}
                            </small>
                        ) : null}
                    </div>
                    <SovosButton
                        className={styles["admin-scripts-button"]}
                        disabled={!selected || running || !projectId}
                        onClick={handleRun}
                        type="button"
                    >
                        {running ? i18n`Run` + "…" : i18n`Run`}
                    </SovosButton>
                    {result ? (
                        <div className={styles["admin-scripts-success"]}>
                            {i18n`Script started`} — {i18n`Execution id`}: {result.executionId}
                        </div>
                    ) : null}
                    {error ? <div className={styles["admin-scripts-error"]}>{error}</div> : null}
                </>
            ) : null}
        </div>
    );
};

export default AdminScriptsTab;
